import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { Listing, Offer } from '../types';
import './ListingDetail.css';

const ListingDetail: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState<Listing | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({ mlsNumber: '', listingPrice: '', listingDate: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!currentUser || !listingId) return;

    const fetchListingAndOffers = async () => {
      try {
        setLoading(true);

        // Fetch listing details
        const listingDoc = await getDoc(doc(db, 'listings', listingId));
        if (listingDoc.exists()) {
          const data = listingDoc.data();
          setListing({
            id: listingDoc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Listing);
        }

        // Fetch offers for this listing
        const offersQuery = query(
          collection(db, 'offers'),
          where('listingId', '==', listingId),
          where('userId', '==', currentUser.uid)
        );
        const offersSnapshot = await getDocs(offersQuery);
        const offersData: Offer[] = [];
        
        offersSnapshot.forEach((doc) => {
          const offer = {
            id: doc.id,
            ...doc.data(),
            submissionDate: doc.data().submissionDate?.toDate()
          } as Offer;
          
          if (offer.parsingStatus === 'completed') {
            offersData.push(offer);
          }
        });

        // Sort by submission date
        offersData.sort((a, b) => (b.submissionDate?.getTime() || 0) - (a.submissionDate?.getTime() || 0));
        setOffers(offersData);
      } catch (error) {
        console.error('Error fetching listing details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListingAndOffers();
  }, [currentUser, listingId]);

  const handleDeleteListing = async () => {
    if (!listing?.id) return;
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'listings', listing.id));
      navigate('/listings');
    } catch (err) {
      console.error('Error deleting listing:', err);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleStartEdit = () => {
    setEditValues({
      mlsNumber: listing?.mlsNumber || '',
      listingPrice: listing?.listingPrice?.toString() || '',
      listingDate: listing?.listingDate || '',
    });
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!listing?.id) return;
    setIsSaving(true);
    try {
      const updates = {
        mlsNumber: editValues.mlsNumber || null,
        listingPrice: editValues.listingPrice ? parseFloat(editValues.listingPrice) : null,
        listingDate: editValues.listingDate || null,
        updatedAt: Timestamp.now(),
      };
      await updateDoc(doc(db, 'listings', listing.id), updates);
      setListing(prev => prev ? { ...prev, ...updates, updatedAt: new Date() } : prev);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving listing fields:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getScoreClass = (score: number) => {
    if (score >= 7) return 'excellent';
    if (score >= 5) return 'good';
    if (score >= 3) return 'fair';
    return 'poor';
  };

  if (loading) {
    return (
      <div className="listing-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading listing details...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="listing-detail-container">
        <div className="empty-state">
          <h3>Listing Not Found</h3>
          <p>The listing you're looking for doesn't exist or you don't have access to it.</p>
          <button className="primary-button" onClick={() => navigate('/dashboard/listings')}>
            Back to Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="listing-detail-container">
      {/* Header */}
      <div className="listing-detail-header">
        <div className="listing-detail-header-top">
          <button className="back-button" onClick={() => navigate('/listings')}>
            ← Back to Listings
          </button>
          {!showDeleteConfirm && (
            <button className="delete-listing-btn" onClick={() => setShowDeleteConfirm(true)}>
              🗑 Delete Listing
            </button>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-banner">
            <div className="delete-confirm-text">
              <strong>Delete this listing?</strong>
              <span>This cannot be undone. Submitted offers will not be deleted.</span>
            </div>
            <div className="delete-confirm-actions">
              <button className="delete-confirm-btn" onClick={handleDeleteListing} disabled={isDeleting}>
                {isDeleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
              <button className="delete-cancel-btn" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="listing-title-section">
          <div className="title-with-status">
            <h1>{listing.address}</h1>
            <span className={`status-badge-inline ${listing.status || 'active'}`}>
              {listing.status || 'active'}
            </span>
          </div>
          <p className="listing-location">{listing.city}, {listing.state} {listing.zipCode}</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="listing-detail-grid">
        {/* Property Details Card */}
        <div className="detail-card">
          <div className="card-header">
            <h2>Property Details</h2>
            <div className="card-header-actions">
              {listing.mlsNumber && !isEditing && (
                <span className="mls-badge">MLS# {listing.mlsNumber}</span>
              )}
              {isEditing ? (
                <>
                  <button className="ld-save-btn" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Saving…' : '✓ Save'}
                  </button>
                  <button className="ld-cancel-btn" onClick={() => setIsEditing(false)} disabled={isSaving}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="ld-edit-btn" onClick={handleStartEdit}>✏️ Edit</button>
              )}
            </div>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>MLS #</label>
              {isEditing ? (
                <input
                  className="ld-edit-input"
                  type="text"
                  placeholder="e.g. 12345"
                  value={editValues.mlsNumber}
                  onChange={e => setEditValues(v => ({ ...v, mlsNumber: e.target.value }))}
                  disabled={isSaving}
                />
              ) : (
                <span>{listing.mlsNumber || '—'}</span>
              )}
            </div>
            <div className="detail-item">
              <label>Listing Price</label>
              {isEditing ? (
                <input
                  className="ld-edit-input"
                  type="number"
                  placeholder="e.g. 450000"
                  value={editValues.listingPrice}
                  onChange={e => setEditValues(v => ({ ...v, listingPrice: e.target.value }))}
                  disabled={isSaving}
                />
              ) : (
                <span className="value-highlight">{formatCurrency(listing.listingPrice)}</span>
              )}
            </div>
            {listing.bedrooms && (
              <div className="detail-item">
                <label>Bedrooms</label>
                <span>{listing.bedrooms}</span>
              </div>
            )}
            {listing.bathrooms && (
              <div className="detail-item">
                <label>Bathrooms</label>
                <span>{listing.bathrooms}</span>
              </div>
            )}
            {listing.squareFeet && (
              <div className="detail-item">
                <label>Square Feet</label>
                <span>{listing.squareFeet.toLocaleString()} sq ft</span>
              </div>
            )}
            {listing.lotSize && (
              <div className="detail-item">
                <label>Lot Size</label>
                <span>{listing.lotSize}</span>
              </div>
            )}
            {listing.yearBuilt && (
              <div className="detail-item">
                <label>Year Built</label>
                <span>{listing.yearBuilt}</span>
              </div>
            )}
            {listing.propertyType && (
              <div className="detail-item">
                <label>Property Type</label>
                <span>{listing.propertyType}</span>
              </div>
            )}
          </div>
        </div>

        {/* Listing Information Card */}
        <div className="detail-card">
          <div className="card-header">
            <h2>Listing Information</h2>
          </div>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Listed Date</label>
              {isEditing ? (
                <input
                  className="ld-edit-input"
                  type="date"
                  value={editValues.listingDate}
                  onChange={e => setEditValues(v => ({ ...v, listingDate: e.target.value }))}
                  disabled={isSaving}
                />
              ) : (
                <span>{listing.listingDate ? formatDate(listing.listingDate) : '—'}</span>
              )}
            </div>
            {listing.daysOnMarket !== null && listing.daysOnMarket !== undefined && (
              <div className="detail-item">
                <label>Days on Market</label>
                <span className="dom-badge">{listing.daysOnMarket} days</span>
              </div>
            )}
            {listing.area && (
              <div className="detail-item">
                <label>Area/Neighborhood</label>
                <span>{listing.area}</span>
              </div>
            )}
            {listing.transType && (
              <div className="detail-item">
                <label>Transaction Type</label>
                <span>{listing.transType}</span>
              </div>
            )}
            {listing.shortSale !== null && listing.shortSale !== undefined && (
              <div className="detail-item">
                <label>Short Sale</label>
                <span>{listing.shortSale ? 'Yes' : 'No'}</span>
              </div>
            )}
            <div className="detail-item">
              <label>Created</label>
              <span>{listing.createdAt ? formatDate(listing.createdAt.toISOString()) : 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Offers Summary Card */}
        <div className="detail-card full-width">
          <div className="card-header">
            <h2>Offers Summary</h2>
            <span className="offers-count">{offers.length} {offers.length === 1 ? 'Offer' : 'Offers'}</span>
          </div>
          
          {offers.length === 0 ? (
            <div className="empty-offers">
              <p>No offers submitted yet for this listing.</p>
            </div>
          ) : (
            <div className="offers-table-wrapper">
              <table className="offers-table">
                <thead>
                  <tr>
                    <th>Submitted</th>
                    <th>Buyer Agent</th>
                    <th>Purchase Price</th>
                    <th>Loan Type</th>
                    <th>Down Payment</th>
                    <th>PTC Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer) => {
                    // v2 schema (extractedData) with v1 fallback (combinedOfferData)
                    const purchasePrice = offer.extractedData?.transaction?.final_sales_price
                      ?? offer.combinedOfferData?.purchaseAgreement?.financial?.purchasePrice;
                    const loanType = offer.extractedData?.financing?.mortgage_type
                      || offer.combinedOfferData?.preApproval?.loan?.loanType;
                    const downPayment = offer.extractedData?.financing?.down_payment_amount
                      ?? offer.combinedOfferData?.purchaseAgreement?.financial?.downPayment;
                    const ptcScore = offer.ptcScore;

                    return (
                      <tr key={offer.id}>
                        <td>{offer.submissionDate ? formatDate(offer.submissionDate.toISOString()) : 'N/A'}</td>
                        <td>
                          <div className="agent-cell">
                            <span className="agent-name">{offer.buyerAgentName || 'Unknown'}</span>
                            <span className="agent-email">{offer.buyerAgentEmail}</span>
                          </div>
                        </td>
                        <td className="currency-cell">
                          {formatCurrency(purchasePrice)}
                          {(offer.extractedData?.escalation_clause?.present || offer.extractedData?.appraisal_guarantee?.present) && (
                            <div className="price-clause-badges">
                              {offer.extractedData?.escalation_clause?.present && (
                                <span className="price-clause-badge price-clause-escalation">⚡ Escalation</span>
                              )}
                              {offer.extractedData?.appraisal_guarantee?.present && (
                                <span className="price-clause-badge price-clause-appraisal">🛡️ Gap</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td>{loanType || 'N/A'}</td>
                        <td className="currency-cell">{formatCurrency(downPayment)}</td>
                        <td>
                          {ptcScore ? (
                            <span className={`score-badge ${getScoreClass(ptcScore)}`}>
                              {ptcScore.toFixed(1)}
                            </span>
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="view-button"
                            onClick={() => navigate(`/offers/${offer.id}`)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>


      </div>
    </div>
  );
};

export default ListingDetail;
